import React, { useState, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Campaign as CampaignIcon,
  AutoAwesome as AutoAwesomeIcon,
  Science as TestIcon,
  CloudUpload as UploadIcon,
  Google as GoogleIcon,
  Facebook as FacebookIcon,
  MusicNote as TikTokIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import AIReasoningDisplay from "./AIReasoningDisplay";
import DateRangePickerModal from "./DateRangePickerModal";
import campaignService from "../services/campaignService";

function Campaign() {
  // Form state
  const [campaignName, setCampaignName] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
  ); // 30 days from now
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // AI-generated fields
  const [aiData, setAiData] = useState(null);
  const [reasoning, setReasoning] = useState(null);
  const [editedData, setEditedData] = useState(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false);

  const handleDateRangeApply = (newStartDate, newEndDate) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUseSampleData = async () => {
    setCampaignName("Summer Sale 2025");
    setStartDate(new Date());
    setEndDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    setWebsiteUrl("https://example.com/products");

    // Load sample image from public directory
    try {
      const response = await fetch("/sample.jpg");
      if (response.ok) {
        const blob = await response.blob();
        const file = new File([blob], "sample.jpg", {
          type: "image/jpeg",
        });
        setImageFile(file);
        
        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } else {
        console.error("Failed to load sample image:", response.status);
      }
    } catch (err) {
      // If sample image fails, just continue with other fields
      console.log("Sample image not available:", err);
    }

    setSuccess("Sample data loaded successfully!");
  };

  const reasoningRef = useRef(null);

  const handleGenerateWithAI = async () => {
    if (!campaignName || !websiteUrl || !imageFile) {
      setError(
        "Please fill in all required fields before generating campaigns"
      );
      return;
    }

    setIsLoading(true);
    try {
      // First, get AI-generated campaign details
      const result = await campaignService.prefillCampaign({
        campaignName,
        startDate,
        endDate,
        websiteUrl,
        imageFile,
      });

      setAiData(result.data);
      setReasoning(result.reasoning);
      // Deep copy for editing with proper structure initialization
      const dataForEditing = JSON.parse(JSON.stringify(result.data));
      // Ensure google_ads object exists
      if (!dataForEditing.google_ads) {
        dataForEditing.google_ads = {
          headlines: [],
          descriptions: [],
          keywords: [],
          campaign_type: '',
          bidding_strategy: ''
        };
      }
      // Ensure budget object exists
      if (!dataForEditing.budget) {
        dataForEditing.budget = {
          total: 0,
          google_ads: 0,
          meta: 0,
          tiktok: 0
        };
      }
      setEditedData(dataForEditing);

      // Then generate the campaigns
      await campaignService.generateCampaigns({
        campaignName,
        startDate,
        endDate,
        websiteUrl,
        imageFile,
        aiData: result.data,
      });

      setShowResults(true);
      setSuccess("Campaigns generated successfully with AI!");

      // Scroll to AI Reasoning section
      setTimeout(() => {
        reasoningRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (err) {
      setError("Failed to generate campaigns: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const platformIcons = {
    google: <GoogleIcon />,
    meta: <FacebookIcon />,
    tiktok: <TikTokIcon />,
  };

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CampaignIcon
              sx={{
                fontSize: 48,
                color: "primary.main",
                mr: 2,
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Universal Campaign Creator
            </Typography>
          </Box>

          <Typography variant="h6" color="text.secondary">
            Create campaigns across multiple platforms with AI-powered
            optimization
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Fill in basic details once, and let AI generate platform-specific
            campaigns
          </Typography>
        </Box>
      </motion.div>

      {/* Form Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Paper
          sx={{
            p: 4,
            background: "rgba(30, 41, 59, 0.8)",
            backdropFilter: "blur(10px)",
            border: "1px solid",
            borderColor: "primary.dark",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Campaign Details
            </Typography>
            <Button
              variant="outlined"
              startIcon={<TestIcon />}
              onClick={handleUseSampleData}
              sx={{
                borderColor: "warning.main",
                color: "warning.main",
                "&:hover": {
                  borderColor: "warning.light",
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                },
              }}
            >
              Use Sample Data
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Campaign Name */}
            <TextField
              fullWidth
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g., Summer Sale 2025"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />

            {/* Date Range */}
            <Box>
              <TextField
                fullWidth
                label="Campaign Duration"
                value={
                  startDate && endDate
                    ? `${format(startDate, "MMM dd, yyyy")} - ${format(
                        endDate,
                        "MMM dd, yyyy"
                      )}`
                    : ""
                }
                placeholder="Select date range"
                onClick={() => setDateRangeModalOpen(true)}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{
                  cursor: "pointer",
                  "& .MuiOutlinedInput-root": {
                    "&:hover fieldset": {
                      borderColor: "primary.main",
                    },
                  },
                }}
              />
            </Box>

            {/* Website URL */}
            <TextField
              fullWidth
              label="Website/Landing Page URL"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com/products"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />

            {/* Image Upload */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Campaign Image
              </Typography>

              {!imagePreview ? (
                <Box
                  sx={{
                    border: "2px dashed",
                    borderColor: "primary.main",
                    borderRadius: 2,
                    p: 4,
                    textAlign: "center",
                    bgcolor: "rgba(99, 102, 241, 0.05)",
                    cursor: "pointer",
                    transition: "all 0.3s",
                    "&:hover": {
                      bgcolor: "rgba(99, 102, 241, 0.1)",
                      borderColor: "primary.light",
                    },
                  }}
                >
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="image-upload"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="image-upload" style={{ cursor: "pointer" }}>
                    <UploadIcon
                      sx={{ fontSize: 48, color: "primary.main", mb: 2 }}
                    />
                    <Typography variant="h6" color="primary">
                      Click to upload image
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or drag and drop
                    </Typography>
                  </label>
                </Box>
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    textAlign: "center",
                    p: 2,
                    border: "1px solid",
                    borderColor: "primary.dark",
                    borderRadius: 2,
                    bgcolor: "rgba(99, 102, 241, 0.05)",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="Campaign preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      borderRadius: "8px",
                      display: "block",
                      margin: "0 auto",
                    }}
                  />
                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="image-replace"
                    type="file"
                    onChange={handleImageUpload}
                  />
                  <label htmlFor="image-replace">
                    <Button
                      component="span"
                      variant="outlined"
                      size="small"
                      startIcon={<UploadIcon />}
                      sx={{
                        mt: 2,
                        borderColor: "primary.main",
                        color: "primary.main",
                        "&:hover": {
                          borderColor: "primary.light",
                          bgcolor: "rgba(99, 102, 241, 0.1)",
                        },
                      }}
                    >
                      Replace Image
                    </Button>
                  </label>
                </Box>
              )}
            </Box>

            {/* Generate Campaigns with AI Button */}
            <Box
              sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
            >
              <Button
                variant="contained"
                startIcon={
                  isLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <AutoAwesomeIcon />
                  )
                }
                onClick={handleGenerateWithAI}
                disabled={
                  isLoading || !campaignName || !websiteUrl || !imageFile
                }
                sx={{
                  background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  minWidth: "250px",
                  "&:hover": {
                    background: "linear-gradient(135deg, #818CF8, #A78BFA)",
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  },
                }}
              >
                {isLoading
                  ? "Generating Campaigns..."
                  : "Generate Campaigns with AI"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* AI Reasoning Display */}
      <AnimatePresence>
        {reasoning && (
          <motion.div
            ref={reasoningRef}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <Box sx={{ mb: 3 }}>
              <AIReasoningDisplay
                reasoning={reasoning}
                aiData={aiData}
                isLoading={isLoading && !reasoning}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Campaign Results */}
      <AnimatePresence>
        {showResults && aiData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Paper
              sx={{
                background: "rgba(30, 41, 59, 0.6)",
                backdropFilter: "blur(10px)",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  p: 3,
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CampaignIcon sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Generated Campaigns
                  </Typography>
                  <Chip label={`3 platforms`} size="small" sx={{ ml: 1 }} />
                  <Chip
                    label={`$${aiData.budget?.total || 0} total`}
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: "rgba(76, 175, 80, 0.2)",
                      color: "#4CAF50",
                      fontWeight: 600,
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  sx={{
                    textTransform: "none",
                    background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #818CF8, #A78BFA)",
                    },
                  }}
                >
                  Export All
                </Button>
              </Box>

              {/* Main Content Area */}
              <Box sx={{ display: "flex", minHeight: 500 }}>
                {/* Left: Platform Tabs */}
                <Box
                  sx={{
                    width: 200,
                    borderRight: "1px solid rgba(255, 255, 255, 0.1)",
                    bgcolor: "rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Tabs
                    orientation="vertical"
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                    sx={{
                      "& .MuiTabs-indicator": {
                        left: 0,
                        width: 3,
                      },
                      "& .MuiTab-root": {
                        alignItems: "flex-start",
                        textAlign: "left",
                        minHeight: 80,
                        px: 2,
                        py: 2,
                        borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                        "&.Mui-selected": {
                          bgcolor: "rgba(99, 102, 241, 0.1)",
                        },
                      },
                    }}
                  >
                    <Tab
                      icon={platformIcons.google}
                      iconPosition="start"
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            Google Ads
                          </Typography>
                          <Chip
                            label={`$${aiData.budget?.google_ads || 0}`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: "rgba(76, 175, 80, 0.2)",
                              color: "#4CAF50",
                              height: 20,
                            }}
                          />
                        </Box>
                      }
                    />
                    <Tab
                      icon={platformIcons.meta}
                      iconPosition="start"
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            Meta
                          </Typography>
                          <Chip
                            label={`$${aiData.budget?.meta || 0}`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: "rgba(76, 175, 80, 0.2)",
                              color: "#4CAF50",
                              height: 20,
                            }}
                          />
                        </Box>
                      }
                    />
                    <Tab
                      icon={platformIcons.tiktok}
                      iconPosition="start"
                      label={
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            TikTok
                          </Typography>
                          <Chip
                            label={`$${aiData.budget?.tiktok || 0}`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: "rgba(76, 175, 80, 0.2)",
                              color: "#4CAF50",
                              height: 20,
                            }}
                          />
                        </Box>
                      }
                    />
                  </Tabs>
                </Box>

                {/* Right: Campaign Details */}
                <Box sx={{ flex: 1, p: 3, overflowY: "auto" }}>
                  {activeTab === 0 && editedData?.google_ads && (
                    <motion.div
                      key="google"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <GoogleIcon
                            sx={{ color: "primary.main", fontSize: 32 }}
                          />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Google Ads Campaign
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Search & Display Network
                            </Typography>
                          </Box>
                        </Box>

                        {/* Campaign Fields */}
                        <Paper
                          sx={{
                            p: 3,
                            bgcolor: "rgba(99, 102, 241, 0.05)",
                            border: "1px solid rgba(99, 102, 241, 0.2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Campaign Budget"
                              value={`$${
                                editedData.budget?.google_ads || 0
                              }`}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.budget.google_ads =
                                  parseInt(e.target.value.replace("$", "")) ||
                                  0;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Campaign Type"
                              value={editedData.google_ads?.campaign_type || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.google_ads.campaign_type = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Headlines (separated by |)"
                              value={
                                editedData.google_ads?.headlines?.join(" | ") || ""
                              }
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.google_ads.headlines =
                                  e.target.value.split(" | ");
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Descriptions (separated by |)"
                              value={
                                editedData.google_ads?.descriptions?.join(" | ") ||
                                ""
                              }
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.google_ads.descriptions =
                                  e.target.value.split(" | ");
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Keywords (comma separated)"
                              value={
                                editedData.google_ads?.keywords?.join(", ") || ""
                              }
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.google_ads.keywords =
                                  e.target.value.split(", ");
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    </motion.div>
                  )}

                  {activeTab === 1 && editedData?.meta && (
                    <motion.div
                      key="meta"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <FacebookIcon
                            sx={{ color: "primary.main", fontSize: 32 }}
                          />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Meta Campaign
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Facebook & Instagram Ads
                            </Typography>
                          </Box>
                        </Box>

                        {/* Campaign Fields */}
                        <Paper
                          sx={{
                            p: 3,
                            bgcolor: "rgba(99, 102, 241, 0.05)",
                            border: "1px solid rgba(99, 102, 241, 0.2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Campaign Budget"
                              value={`$${
                                editedData.budget?.meta || 0
                              }`}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.budget.meta =
                                  parseInt(e.target.value.replace("$", "")) ||
                                  0;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Headline"
                              value={editedData.meta?.headline || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.meta.headline = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Primary Text"
                              value={editedData.meta?.primary_text || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.meta.primary_text = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={3}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Call to Action"
                              value={editedData.meta?.call_to_action || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.meta.call_to_action = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Target Interests (comma separated)"
                              value={
                                editedData.meta?.interests?.join(", ") || ""
                              }
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.meta.interests =
                                  e.target.value.split(", ");
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    </motion.div>
                  )}

                  {activeTab === 2 && editedData?.tiktok && (
                    <motion.div
                      key="tiktok"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 3,
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          <TikTokIcon
                            sx={{ color: "primary.main", fontSize: 32 }}
                          />
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              TikTok Campaign
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Video & Creative Ads
                            </Typography>
                          </Box>
                        </Box>

                        {/* Campaign Fields */}
                        <Paper
                          sx={{
                            p: 3,
                            bgcolor: "rgba(99, 102, 241, 0.05)",
                            border: "1px solid rgba(99, 102, 241, 0.2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <TextField
                              label="Campaign Budget"
                              value={`$${
                                editedData.budget?.tiktok || 0
                              }`}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.budget.tiktok =
                                  parseInt(e.target.value.replace("$", "")) ||
                                  0;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Target Age Range"
                              value={editedData.tiktok?.target_age || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.tiktok.target_age = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Caption"
                              value={editedData.tiktok?.caption || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.tiktok.caption = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={3}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Hashtags (space separated)"
                              value={
                                editedData.tiktok?.hashtags?.join(" ") || ""
                              }
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.tiktok.hashtags =
                                  e.target.value.split(" ");
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              multiline
                              rows={2}
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                            <TextField
                              label="Content Theme"
                              value={editedData.tiktok?.content_style || ""}
                              onChange={(e) => {
                                const newData = { ...editedData };
                                newData.tiktok.content_style = e.target.value;
                                setEditedData(newData);
                              }}
                              size="small"
                              fullWidth
                              InputProps={{
                                sx: { bgcolor: "rgba(0,0,0,0.2)" },
                              }}
                            />
                          </Box>
                        </Paper>
                      </Box>
                    </motion.div>
                  )}
                </Box>
              </Box>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Date Range Modal */}
      <DateRangePickerModal
        open={dateRangeModalOpen}
        onClose={() => setDateRangeModalOpen(false)}
        startDate={startDate}
        endDate={endDate}
        onApply={handleDateRangeApply}
      />

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Campaign;
